import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Gift, Flame, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const InboxPanel = ({ gameId, onRefresh }) => {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [dailyGift, setDailyGift] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [giftClaimed, setGiftClaimed] = useState(false);
  const [collecting, setCollecting] = useState(null);

  const loadInbox = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/store/inbox/${gameId}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        setUnread(data.unread_count || 0);
      }
    } catch (e) { console.error(e); }
  }, [gameId]);

  useEffect(() => { loadInbox(); }, [loadInbox]);

  const claimDailyGift = async () => {
    if (claiming || giftClaimed) return;
    setClaiming(true);
    try {
      const res = await fetch(`${API}/api/store/inbox/daily-gift/${gameId}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setDailyGift(data);
        setGiftClaimed(true);
        loadInbox();
        if (onRefresh) onRefresh();
      } else {
        // Already claimed today
        setGiftClaimed(true);
        setDailyGift({ already_claimed: true, message: data.message });
      }
    } catch (e) { console.error(e); }
    setClaiming(false);
  };

  // Auto-claim on mount
  useEffect(() => {
    if (gameId && !giftClaimed) {
      claimDailyGift();
    }
  }, [gameId]);

  const handleCollect = async (msgId) => {
    setCollecting(msgId);
    try {
      const res = await fetch(`${API}/api/store/inbox/collect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, message_id: msgId }),
      });
      if (res.ok) {
        loadInbox();
        if (onRefresh) onRefresh();
      }
    } catch (e) { console.error(e); }
    setCollecting(null);
  };

  const handleRead = async (msgId) => {
    await fetch(`${API}/api/store/inbox/read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: gameId, message_id: msgId }),
    });
    loadInbox();
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'daily_gift': return '#C9A84C';
      case 'achievement': return '#4ADE80';
      case 'purchase': return '#9F7AEA';
      case 'system': return '#4A7AB5';
      default: return '#E8E0D0';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'daily_gift': return 'DAILY GIFT';
      case 'achievement': return 'ACHIEVEMENT';
      case 'purchase': return 'PURCHASE';
      case 'system': return 'SYSTEM';
      default: return 'MESSAGE';
    }
  };

  const formatReward = (reward) => {
    if (!reward || Object.keys(reward).length === 0) return null;
    const parts = [];
    if (reward.cash) parts.push(`$${reward.cash.toLocaleString()}`);
    if (reward.energy) parts.push(`+${reward.energy}E`);
    if (reward.energy_full) parts.push('Full Energy');
    if (reward.xp) parts.push(`+${reward.xp}XP`);
    if (reward.crystals) parts.push(`+${reward.crystals} Crystals`);
    if (reward.influence) parts.push(`+${reward.influence}IP`);
    if (reward.job_success_pct) parts.push(`+${reward.job_success_pct}% Success`);
    if (reward.income_pct) parts.push(`+${reward.income_pct}% Income`);
    return parts.join(' · ');
  };

  return (
    <div data-testid="inbox-panel">
      <div className="flex items-center gap-2 mb-4">
        <Mail size={16} className="text-[#C9A84C]" />
        <h3 className="font-['Cormorant_Garamond'] text-[#C9A84C] text-lg font-bold">INBOX</h3>
        {unread > 0 && <span className="bg-[#8B1A1A] text-[#E8E0D0] text-[9px] font-bold px-1.5 py-0.5 rounded-full">{unread}</span>}
      </div>

      {/* Daily Gift Banner */}
      <AnimatePresence>
        {dailyGift && dailyGift.success && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="bg-gradient-to-r from-[#C9A84C]/10 to-[#C9A84C]/5 border border-[#C9A84C]/30 rounded-lg p-4 mb-4"
            data-testid="daily-gift-banner"
          >
            <div className="flex items-center gap-2 mb-2">
              <Gift size={16} className="text-[#C9A84C]" />
              <span className="font-['Outfit'] text-[#C9A84C] text-xs font-bold tracking-widest">
                {dailyGift.gift_title}
              </span>
              {dailyGift.streak >= 3 && (
                <span className="flex items-center gap-1 px-1.5 py-0.5 bg-[#8B1A1A]/15 border border-[#8B1A1A]/20 rounded text-[#8B1A1A] text-[8px] font-['Outfit'] tracking-widest">
                  <Flame size={8} /> {dailyGift.streak} DAY STREAK
                </span>
              )}
            </div>
            <p className="font-['Outfit'] text-[#E8E0D0]/50 text-xs mb-2">{dailyGift.gift_body}</p>
            <p className="font-['Outfit'] text-[#4ADE80] text-xs font-bold">{dailyGift.reward_desc}</p>
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] mt-2">Collect it from your messages below.</p>
          </motion.div>
        )}
        {dailyGift && dailyGift.already_claimed && (
          <div className="bg-[#111820] border border-[#1A2535] rounded-lg p-3 mb-4 flex items-center gap-2">
            <Gift size={14} className="text-[#E8E0D0]/20" />
            <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs">{dailyGift.message}</p>
          </div>
        )}
      </AnimatePresence>

      {/* Claim button if not auto-claimed */}
      {!giftClaimed && (
        <button onClick={claimDailyGift} disabled={claiming}
          className="w-full mb-4 py-3 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded-lg text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/20 transition-colors disabled:opacity-30"
          data-testid="claim-daily-gift-btn">
          {claiming ? 'OPENING...' : 'CLAIM DAILY GIFT'}
        </button>
      )}

      {/* Messages */}
      {messages.length === 0 ? (
        <div className="text-center py-12">
          <Mail size={24} className="text-[#E8E0D0]/10 mx-auto mb-2" />
          <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs">No messages yet.</p>
          <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/15 italic text-sm mt-1">"Check-in daily. I'll leave you something."</p>
        </div>
      ) : (
        <div className="space-y-2">
          {messages.map(msg => {
            const typeColor = getTypeColor(msg.type);
            const rewardText = formatReward(msg.reward);
            const hasReward = msg.reward && Object.keys(msg.reward).length > 0;

            return (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className={`bg-[#111820] border rounded-lg p-3 transition-all ${
                  !msg.read ? 'border-l-2' : 'border-[#1A2535] opacity-70'
                }`}
                style={{ borderLeftColor: !msg.read ? typeColor : undefined }}
                data-testid={`inbox-msg-${msg.id}`}
                onClick={() => !msg.read && handleRead(msg.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {!msg.read && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: typeColor }} />}
                      <span className="font-['Outfit'] text-[7px] tracking-widest px-1.5 py-0.5 rounded"
                        style={{ color: typeColor, backgroundColor: `${typeColor}10`, border: `1px solid ${typeColor}20` }}>
                        {getTypeLabel(msg.type)}
                      </span>
                      <span className="font-['Outfit'] text-[#E8E0D0]/15 text-[8px]">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h5 className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold">{msg.title}</h5>
                    <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-0.5">{msg.body}</p>
                    {rewardText && (
                      <p className="font-['Outfit'] text-[#4ADE80]/60 text-[10px] mt-1">{rewardText}</p>
                    )}
                  </div>
                  <div className="shrink-0">
                    {hasReward && !msg.collected ? (
                      <button onClick={(e) => { e.stopPropagation(); handleCollect(msg.id); }}
                        disabled={collecting === msg.id}
                        className="px-3 py-1.5 rounded border border-[#C9A84C]/30 bg-[#C9A84C]/10 text-[#C9A84C] font-['Outfit'] text-[9px] tracking-widest hover:bg-[#C9A84C]/20 disabled:opacity-30 transition-colors"
                        data-testid={`collect-${msg.id}`}>
                        {collecting === msg.id ? '...' : 'COLLECT'}
                      </button>
                    ) : msg.collected && hasReward ? (
                      <span className="font-['Outfit'] text-[#4ADE80]/40 text-[8px] tracking-widest">COLLECTED</span>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InboxPanel;
